import {
  EventMessageComponent,
  FileAttachment,
  LiveChatSendAgentMessageResponse,
  LiveChatSendMessageResponse,
  UpdateForethoughtLiveChatHelpdeskConversationIdResponse,
  WidgetComponent,
  isButtonsComponent,
  isEventMessageComponent,
  isTextMessageComponent,
} from 'api/types';
import {
  addWidgetComponents,
  deleteForethoughtLiveChatWidgetComponents,
} from 'reducers/workflows';
import ApiService from 'services/ApiService';
import { createAppAsyncThunk } from 'createAppAsyncThunk';
import { setIsAgentTyping } from 'reducers/live-chat';
import { debounce } from 'utils/debounce';
import { rejectWithCustomFetchError } from 'api/customFetch';

export const sendUserReply = createAppAsyncThunk(
  'liveChat/sendUserMessage',
  async (
    {
      addWidgetComponent = true,
      attachments = [],
      message,
    }: {
      addWidgetComponent?: boolean;
      attachments?: FileAttachment[];
      message: string;
    },
    { dispatch, getState, rejectWithValue },
  ): Promise<LiveChatSendMessageResponse> => {
    const state = getState();
    const isRedactSrpEnabled =
      state.workflows.enabled_feature_flags.includes('redact_srp_words');

    /**
     * Add empty text message component to not render agent
     * text messages but render all user messages. Widget
     * is built to have 1 widget component and 1 query. This
     * allows multiple user queries back to back
     */

    if (addWidgetComponent && !isRedactSrpEnabled) {
      dispatch(
        addWidgetComponents([
          {
            component_fields: { attachments, message },
            component_type: 'user_message',
            isSent: true,
          },
        ]),
      );
    }

    try {
      const data = await ApiService.request('live-chat-send-message', {
        data: { attachments, message },
        urlValues: {
          conversationId: state.workflows.conversationId,
          helpdeskConversationId: state.liveChat.chatId,
        },
      });

      if (addWidgetComponent && isRedactSrpEnabled) {
        dispatch(
          addWidgetComponents([
            {
              component_fields: { attachments, message: data.user_reply },
              component_type: 'user_message',
              isSent: true,
            },
          ]),
        );
      }

      return data;
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

/**
 * appends message & attachments to ForethoughtLiveChat mongo document
 */
export const sendAgentReply = createAppAsyncThunk(
  'liveChat/sendAgentMessage',
  async (
    {
      attachments = [],
      message,
    }: {
      attachments?: FileAttachment[];
      message: string;
    },
    { getState, rejectWithValue },
  ): Promise<LiveChatSendAgentMessageResponse> => {
    const state = getState();

    try {
      return await ApiService.request('live-chat-send-agent-message', {
        data: { attachments, message },
        urlValues: {
          conversationId: state.workflows.conversationId,
          helpdeskConversationId: state.liveChat.chatId,
        },
      });
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

/**
 * Filters out duplicate components using timestamp
 * and dispatches sendAgentReply if ForethoughtLiveChat document
 * transcript should be updated with message
 */
export const addLiveChatWidgetComponents = createAppAsyncThunk(
  'liveChat/addWidgetComponents',
  async (
    {
      components,
      helpdeskConversationId,
      sendAgentMessage = false,
    }: {
      components: WidgetComponent[];
      helpdeskConversationId?: string;
      sendAgentMessage?: boolean;
    },
    { dispatch, getState },
  ) => {
    const state = getState();

    /**
     * Check is used by Kustomer One Chat because there can be message events received
     * that belong to the same user but a different conversation that we are not controlling.
     */
    if (
      helpdeskConversationId &&
      state.liveChat.chatId !== helpdeskConversationId
    ) {
      return;
    }

    const filteredComponents = components.filter(component => {
      // if identical text message component with timestamp is already added, don't add it again
      if (isTextMessageComponent(component) && component.timestamp) {
        return !state.workflows.widgetComponents.find(wc => {
          if (isTextMessageComponent(wc) && wc.timestamp) {
            return (
              wc.timestamp === component.timestamp &&
              wc.component_fields.message === component.component_fields.message
            );
          }

          return false;
        });
      }

      // if identical event message component with timestamp is already added, don't add it again
      if (isEventMessageComponent(component) && component.timestamp) {
        return !state.workflows.widgetComponents.find(wc => {
          if (isEventMessageComponent(wc) && wc.timestamp) {
            return (
              wc.timestamp === component.timestamp &&
              wc.component_fields.message === component.component_fields.message
            );
          }

          return false;
        });
      }

      // if identical button component with timestamp is already added, don't add it again
      if (isButtonsComponent(component) && component.timestamp) {
        return !state.workflows.widgetComponents.find(wc => {
          if (isButtonsComponent(wc) && wc.timestamp) {
            return wc.timestamp === component.timestamp;
          }

          return false;
        });
      }

      // include all other components
      return true;
    });

    if (filteredComponents.length > 0) {
      dispatch(addWidgetComponents(filteredComponents));

      if (sendAgentMessage) {
        filteredComponents.forEach(component => {
          if (isTextMessageComponent(component)) {
            dispatch(
              sendAgentReply({
                attachments: component.component_fields.attachments,
                message: component.component_fields.message,
              }),
            );
          } else if (
            isButtonsComponent(component) &&
            component.component_fields.prompt
          ) {
            dispatch(
              sendAgentReply({
                message: component.component_fields.prompt,
              }),
            );
          }
        });
      }
    }
  },
);

/**
 * Updates ForethoughtLiveChat document with helpdesk conversation id
 * that was created on the frontend. Currently only used by Kustomer.
 */
export const updateForethoughtLiveChatHelpdeskConversationId =
  createAppAsyncThunk(
    'liveChat/updateForethoughtLiveChatHelpdeskConversationId',
    async (
      helpdeskConversationId: string,
      { getState, rejectWithValue },
    ): Promise<UpdateForethoughtLiveChatHelpdeskConversationIdResponse> => {
      const state = getState();

      try {
        return await ApiService.request(
          'update-forethought-live-chat-helpdesk-conversation-id',
          {
            data: { helpdesk_conversation_id: helpdeskConversationId },
            urlValues: {
              conversationId: state.workflows.conversationId,
            },
          },
        );
      } catch (error) {
        return rejectWithCustomFetchError(error, rejectWithValue);
      }
    },
  );

/**
 * Deletes the existing ForethoughtLiveChat for a conversation
 * both from redux and the db
 *
 * used by Kustomer and Zopim one chats when availability checks
 * fail on the frontend.
 */
export const deleteForethoughtLiveChat = createAppAsyncThunk(
  'liveChat/deleteForethoughtLiveChat',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState();

    // remove live chat widget components from redux store
    dispatch(deleteForethoughtLiveChatWidgetComponents());

    // remove live chat component from mongo
    try {
      return await ApiService.request('delete-forethought-live-chat', {
        urlValues: {
          conversationId: state.workflows.conversationId,
        },
      });
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const addLiveChatEventMessage = createAppAsyncThunk(
  'liveChat/addEventMessage',
  async (
    { message, timestamp }: { message: string; timestamp?: number },
    { dispatch },
  ) => {
    dispatch(
      addLiveChatWidgetComponents({
        components: [
          {
            component_fields: { message },
            component_type: 'event_message',
            timestamp,
          } satisfies EventMessageComponent,
        ],
      }),
    );
  },
);

const debounceEndAgentTyping = debounce((callback: () => void) => {
  callback();
}, 5000);

/**
 * Sets the agent typing status and debounces to set
 * isAgentTyping to false after 5 seconds no matter what
 */
export const handleAgentTyping = createAppAsyncThunk(
  'liveChat/handleAgentTyping',
  async ({ isTyping }: { isTyping: boolean }, { dispatch }) => {
    dispatch(setIsAgentTyping(isTyping));
    debounceEndAgentTyping(() => {
      if (isTyping) {
        dispatch(setIsAgentTyping(false));
      }
    });
    return Promise.resolve();
  },
);

export const resolveConversation = createAppAsyncThunk(
  'liveChat/resolveConversation',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();

    try {
      return await ApiService.request('live-chat-resolve-conversation', {
        urlValues: {
          conversationId: state.workflows.conversationId,
          helpdeskConversationId: state.liveChat.chatId,
        },
      });
    } catch (error) {
      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);
